import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType } from "@Components/Modules/ErrorHandler";
import { isMailValid, isNullOrEmpty } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { Textbox, TextboxType } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./Newsletter.module.css";

export interface ISubscribeNewsletter extends IComponentAjax<string> {
    description: string;
    successMessage: string;
    emailTextbox: ITextbox;
    submitButton: IButton;
    hasAccount?: boolean;
    emailRequiredError?: IValidator;
    emailInvalidError?: IValidator;
}

interface SubscribeNewsletterState extends IComponentAjaxState {
    showSuccess: boolean;
    email: string;
    successMessage: string;
}

export default class SubscribeNewsletter extends ComponentAjax<
    string,
    ISubscribeNewsletter,
    SubscribeNewsletterState
> {
    public static defaultProps: ISubscribeNewsletter = {
        ...ComponentAjax.defaultProps,
        description: null,
        successMessage: null,
        emailTextbox: null,
        submitButton: null,
        hasAccount: false,
        emailRequiredError: { errorName: "email-input", message: "E-mail address is required" },
        emailInvalidError: {
            errorName: "email-input",
            message: "Please enter a valid e-mail address",
        },
        loading: { ...ComponentAjax.defaultProps.loading, isModal: true },
    };

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        emailRequired: {
            name: "email-newsletter-required",
            type: ErrorType.Validator,
            targetRef: "email-box",
            container: { ref: "email-error-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.email) === false) {
                    return true;
                }
                return false;
            },
        },
        emailInvalid: {
            name: "email-newsletter-invalid",
            type: ErrorType.Validator,
            targetRef: "email-box",
            container: { ref: "email-error-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.email) === true) {
                    return true;
                }
                return isMailValid(this.state.email);
            },
        },
    };

    constructor(props: ISubscribeNewsletter) {
        super(props);
        this.state = {
            isLoading: false,
            showSuccess: false,
            email: this.props.hasAccount ? null : this.props.emailTextbox.value,
            successMessage: null,
        };
    }

    public render() {
        if (this.props.hasAccount) {
            return (
                <div
                    {...this.rootDefaultAttributes(
                        css.subscribeNewsletterComponent,
                        cssPortal.standardContainer,
                    )}
                >
                    <div className={css.description}>
                        <h6>{this.props.description}</h6>
                    </div>
                    <Button {...this.props.submitButton} />
                </div>
            );
        }

        const emailTextbox: ITextbox = {
            ...this.props.emailTextbox,
            value: this.state.email,
            textboxType: TextboxType.Email,
            clientId: "EmailTextbox",
        };
        emailTextbox.onChange = (event) => {
            this.setState({ email: event.target.value });
        };

        const submitClick = () => {
            this.submitButtonClick();
            return false;
        };

        const renderFooter = () => {
            if (this.state.showSuccess === true) {
                return null;
            }

            return (
                <div className={css.footer}>
                    <Textbox {...emailTextbox} ref="email-box" />
                    <ErrorContainer {...this.errors.emailInvalid.container} />
                    <Button {...this.props.submitButton} onClick={submitClick} />
                </div>
            );
        };

        const renderSuccess = () => {
            if (this.state.showSuccess === false) {
                return null;
            }

            return <div>{this.state.successMessage}</div>;
        };

        return (
            <div
                {...this.rootDefaultAttributes(css.subscribeNewsletterComponent, cssPortal.standardContainer)}
            >
                <div className={css.description}>
                    <h6>{this.props.description}</h6>
                </div>
                {renderFooter()}
                {renderSuccess()}
                {this.renderLoading()}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: string) {
        this.setState({ isLoading: false });

        if (response) {
            this.setState({ successMessage: response, showSuccess: true });
        }
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private submitButtonClick() {
        this.ajaxRequest(
            {
                electronicAddress: this.state.email,
            },
            () => {
                this.setState({ isLoading: true });
            },
        );
    }
}
